/* Movora PDP gallery filter.
 *
 * Shows only the SELECTED colourway's supplier photography in the product gallery.
 *
 * The problem it solves: every colourway's front and back photograph is attached to
 * the product, so the Slim Crewneck Tee alone rendered 28 images and the V-Neck Yoga
 * Bra 44. Picking a colour changed which image was active but left all the others in
 * the strip, so the page read as a contact sheet and a shopper who picked Rose Red
 * was still looking at twelve other colours.
 *
 * Dawn's own `hide_variants` setting is not enough here. It only skips media that is
 * ATTACHED to a variant, and on this catalogue only the front view of each colourway
 * is attached -- so it would still have leaked every other colourway's back view.
 *
 * Matching is on data-mv-color, written by snippets/movora-media-color.liquid, and
 * compared with === against the radio's value. Never a substring test: this
 * catalogue has "Pink" and "Light Pink", "White" and "White with Green", and
 * "Rose Red" and "Rose Red with White".
 *
 * data-mv-worn marks model photography. It used to be excluded from the product page
 * outright, because the only worn shots on the store were AI renders that Arie
 * rejected. As of 2026-08-17 the How Lucky Are We line carries REAL studio model
 * photography that he asked to lead everywhere, so a worn shot is now filtered
 * exactly like any other frame: kept when it matches the selected colourway, hidden
 * when it does not. The attribute still exists because the product CARDS read it.
 *
 * That change is what makes the three guards below load-bearing rather than
 * defensive. The worn shot is media position 0, which is the frame Dawn marks
 * .is-active and the only one it loads eagerly -- so hiding it on a colour pick is
 * precisely the case that renders a 715x0 viewer with img.complete === false. All
 * three failures present identically as "thumbnails fine, main viewer empty".
 */
(function () {
  'use strict';

  var SLIDE = '[data-mv-color]';
  // apply() calls setActiveMedia, which moves .is-active -- and the observer below
  // watches class changes. Without this the two would drive each other forever.
  var painting = false;

  // Long enough to outlast Dawn's async variant fetch and its prepend, short
  // enough that a thumbnail click a moment later is respected.
  var WORN_WINDOW_MS = 3000;

  function selectedColor(section) {
    var fs = section.querySelector('fieldset.movora-color-pills');
    if (!fs) return null;
    var checked = fs.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  /* Is model photography allowed to appear on THIS product's page?
   *
   * Default NO. 24 of the 28 products carrying ", worn" media hold AI renders Arie
   * rejected for the product page, and they must stay hidden -- they remain attached
   * only because the product CARDS preview them. sections/main-product.liquid sets
   * this attribute from the `PDP:Worn` product tag, so opting in is per product and
   * the safe answer is the one you get by doing nothing.
   */
  function wornAllowed(section) {
    var el = section.closest ? section.closest('[data-mv-worn-on-pdp]') : null;
    return !!(el || (section.querySelector && section.querySelector('[data-mv-worn-on-pdp]')));
  }

  function apply(section) {
    var items = section.querySelectorAll(SLIDE);
    if (!items.length) return;

    var color = selectedColor(section);
    var allowWorn = wornAllowed(section);

    // Decide first, paint second. If the rule would leave the gallery empty --
    // a colourway with no supplier photograph, or an alt format that changed
    // underneath us -- fall back to showing everything. A busy gallery is a
    // cosmetic problem; a blank one is a broken product page.
    var keep = [];
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      if (!allowWorn && el.hasAttribute('data-mv-worn')) continue;
      if (color !== null && el.getAttribute('data-mv-color') !== color) continue;
      keep.push(el);
    }
    var filtering = keep.length > 0;

    for (var j = 0; j < items.length; j++) {
      var it = items[j];
      var show = !filtering || keep.indexOf(it) !== -1;
      // Inline display beats every stylesheet rule, which matters because Dawn
      // sets display on both .product__media-item and .thumbnail-list__item and
      // would otherwise override the [hidden] attribute.
      it.style.display = show ? '' : 'none';
      if (show) {
        it.removeAttribute('aria-hidden');
        // Dawn marks only the FIRST media eager and lazies the rest. Reveal a lazy
        // one into a slider that is currently zero-height and it never intersects
        // the viewport, so it never loads -- a deadlock, not a slow load. Waiting
        // does nothing; the attribute has to be rewritten.
        var im = it.querySelector('img');
        if (im && im.getAttribute('loading') === 'lazy') im.setAttribute('loading', 'eager');
      } else {
        it.setAttribute('aria-hidden', 'true');
      }
    }

    reactivate(section);

    // Lead with the model shot again after a COLOUR change.
    //
    // A one-shot on the colour changing is NOT enough, and measuring is the only
    // way to see why. Picking Brown ran this, leadWithWorn set the Brown model
    // shot active -- and Dawn then finished its own async variant fetch, prepended
    // the variant's featured image (the supplier flat lay) and made THAT active.
    // The observer called apply again, the colour now equalled the stored one, and
    // the guard skipped. Result: the model shot flashed and the flat lay won.
    //
    // So a colour change opens a short WINDOW instead, and every repaint inside it
    // re-asserts the model shot. The window closes as soon as the active frame is
    // the right one, so this normally runs once or twice.
    //
    // The window is what keeps a THUMBNAIL CLICK working. Always re-asserting would
    // make the flat lays permanently unviewable: clicking one is a class change, the
    // observer repaints, and it would be dragged straight back to the model. Outside
    // the window a deliberate click stands.
    var last = section.getAttribute('data-mv-last-color');
    if (color !== null && color !== last) {
      section.setAttribute('data-mv-last-color', color);
      section.setAttribute('data-mv-worn-until', String(Date.now() + WORN_WINDOW_MS));
    }
    var until = parseInt(section.getAttribute('data-mv-worn-until') || '0', 10);
    if (until && Date.now() < until) {
      // Deliberately does NOT close the window on first success. Measured: it
      // succeeded immediately, closed, and Dawn's variant fetch THEN landed and
      // prepended the flat lay with no window left to correct it. The window has
      // to outlive that fetch, so it runs its full duration and a user click is
      // what cancels it (see wire()).
      leadWithWorn(section);
    } else if (until) {
      section.removeAttribute('data-mv-worn-until');
    }
  }


  /* Dawn's CSS shows ONLY .is-active in the main viewer, and it both prepends the
   * selected variant's featured image and makes that active. If the active frame is
   * one we just hid, the viewer has nothing left to paint and the page renders a
   * full-width strip of zero height. Hand it the first visible frame instead.
   *
   * Match DIRECT children: a nested element inside a media item carries .is-active
   * too, and querySelector('.product__media-list .is-active') returns that one --
   * zero-sized, no alt, display:flex -- which looks healthy while the real active
   * image is hidden.
   */
  function reactivate(section) {
    if (painting) return;
    var gallery = section.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
    var viewer = section.querySelector('.product__media-list');
    if (!viewer) return;

    var kids = viewer.children;
    var active = null;
    var firstVisible = null;
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      var shown = k.style.display !== 'none';
      if (shown && !firstVisible && k.hasAttribute('data-media-id')) firstVisible = k;
      if (k.classList.contains('is-active')) active = k;
    }
    if (!firstVisible) return;
    if (active && active.style.display !== 'none') return;

    painting = true;
    try {
      gallery.setActiveMedia(firstVisible.dataset.mediaId, true);
    } catch (e) {
      /* Never let a gallery tidy-up break the page. */
    }
    painting = false;
  }

  /* Open the page on the model shot.
   *
   * Making the worn frame media position 0 is NOT enough on its own: Dawn prepends
   * the SELECTED VARIANT's featured image and makes that active, so every product
   * here opened on the supplier flat front with the model shot sitting unseen in
   * the thumbnail strip. Measured on the single-colourway Hoodie, where there is no
   * colour ambiguity at all, so this is Dawn's prepend and not a filtering problem.
   *
   * RUNS ON LOAD AND ON EVERY COLOUR CHANGE. It used to be one shot on load, because
   * worn frames existed only for each product's lead colourway and re-running it
   * after a pick would have dragged the viewer back to a model in the wrong colour.
   * That premise died on 2026-08-31: every colourway now has its own front and back
   * model shot, so a pick that does not lead with one shows the supplier flat lay
   * instead of the model the shopper just clicked.
   *
   * The colour equality test is unchanged and is still the accuracy guard. Where a
   * colourway genuinely has no worn frame -- the five moderation rejections, and
   * every Color Block Crop Tank back -- this correctly does nothing and Dawn's own
   * choice stands, rather than opening on a garment the shopper did not select.
   */
  function leadWithWorn(section) {
    if (!wornAllowed(section)) return;
    var gallery = section.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
    var viewer = section.querySelector('.product__media-list');
    if (!viewer) return;

    var color = selectedColor(section);
    var kids = viewer.children;
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (!k.hasAttribute('data-mv-worn')) continue;
      if (k.style.display === 'none') continue;
      if (!k.hasAttribute('data-media-id')) continue;
      if (color !== null && k.getAttribute('data-mv-color') !== color) continue;
      if (k.classList.contains('is-active')) return;
      painting = true;
      try {
        gallery.setActiveMedia(k.dataset.mediaId, true);
      } catch (e) {
        /* Leave Dawn's own choice in place rather than breaking the gallery. */
      }
      painting = false;
      return;
    }
  }

  function wire(section) {
    if (section.dataset.mvGalleryWired === '1') return;
    section.dataset.mvGalleryWired = '1';

    apply(section);

    // Deferred so Dawn's media-gallery has finished its own prepend first --
    // running this synchronously just gets overwritten a tick later.
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        window.setTimeout(function () { leadWithWorn(section); }, 0);
      });
    } else {
      window.setTimeout(function () { leadWithWorn(section); }, 60);
    }

    // Colour picks bubble out of <variant-selects> as a normal change event.
    section.addEventListener('change', function (e) {
      if (e.target && e.target.type === 'radio') apply(section);
    });

    // A single-colourway product fires NO change event. Dawn has the one colour
    // checked on load and clicking an already-checked radio is a no-op, so the
    // Hoodie and the Mock-Neck Sweatshirt would never re-run this at all. Listen
    // for click too, scoped to the colour fieldset so picking a SIZE cannot
    // repaint the gallery.
    var pills = section.querySelector('fieldset.movora-color-pills');
    if (pills) {
      pills.addEventListener('click', function (e) {
        if (e.target && e.target.type === 'radio') apply(section);
      });
    }

    // Dawn re-orders the media list when it sets the active media, and updates
    // the info container asynchronously after fetching the variant's section.
    // Watch for the re-order rather than guessing at a delay.
    //
    // ALSO watch class changes. .is-active moves by a CLASS change, not a
    // childList change, so a childList-only observer never sees Dawn re-assert
    // its own active media after this filter has run -- the filter fires once,
    // Dawn overrides it, and nothing re-runs. `painting` guards the loop this
    // otherwise creates with setActiveMedia.
    // A click anywhere in the gallery is a deliberate choice and cancels the
    // re-assert window immediately. Without this, a thumbnail clicked within
    // WORN_WINDOW_MS of a colour pick gets yanked back to the model shot, which
    // reads as the flat lays being unclickable. Scoped to media-gallery so the
    // colour pills, which live in variant-selects, cannot cancel their own window.
    var gal = section.querySelector('media-gallery');
    if (gal) {
      gal.addEventListener('click', function () {
        section.removeAttribute('data-mv-worn-until');
      }, true);
    }

    var list = section.querySelector('.product__media-list');
    if (list && typeof MutationObserver === 'function') {
      new MutationObserver(function () {
        if (painting) return;
        apply(section);
      }).observe(list, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  function init() {
    var sections = document.querySelectorAll('.product__info-wrapper');
    for (var i = 0; i < sections.length; i++) {
      var root = sections[i].closest('.product') || document;
      wire(root);
    }
    if (!sections.length) {
      var solo = document.querySelector('.product');
      if (solo) wire(solo);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
